import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { decodeRegionFromId } from '@app/shared/helpers/helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Transfer } from '@smash-sdk-private/transfer/01-2024';
import GetDropboxPreviewError from '@smash-sdk-private/transfer/01-2024/types/GetDropboxPreview/GetDropboxPreviewError';
import { config, Region } from '@smash-sdk/core';
import { SmashUploader } from '@smash-sdk/uploader';
import { CustomEventEmitter } from '@smash-sdk/uploader/dist/core/CustomEventEmitter';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { DropboxActions } from '../actions';

@Injectable()
export class DropboxEffects {

  smashUploader: SmashUploader;
  listeners: CustomEventEmitter[] = [];

  constructor(
    private actions$: Actions,
    private store$: Store<fromRoot.State>,
    private router: Router,
  ) { }

  createTeamDropbox$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropboxActions.CreateTeamDropbox),
      withLatestFrom(
        this.store$.pipe(select(fromRoot.getActiveIdentity)),
        this.store$.pipe(select(fromRoot.getCurrentDomain)),
      ),
      concatMap(([action, identity, domain]) => {
        const transferSdk = new Transfer();
        return from(transferSdk.createTeamUserDropbox({
          teamId: action.teamId,
          userId: identity.id,
          configuration: { transfer: domain.options?.dropbox?.transfer } || { transfer: Constant.defaultPremiumDomainSettings.dropbox.transfer },
          availabilityDuration: domain.options?.dropbox?.availabilityDuration?.default || Constant.defaultPremiumDomainSettings.dropbox.availabilityDuration.default,
          maxTransferCount: domain.options?.dropbox?.maxTransferCount?.default || Constant.defaultPremiumDomainSettings.dropbox.maxTransferCount.default,
        })).pipe(
          map((resp) => DropboxActions.CreateTeamDropboxSuccess({ dropbox: resp.dropbox })),
          catchError(error => of(DropboxActions.CreateTeamDropboxFailure({ error: error.message })))
        );
      }))
  );

  getDropboxPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DropboxActions.GetDropboxPreview),
      concatMap((action) => {
        const region = decodeRegionFromId(action.dropboxId);
        config.setRegion(region as Region);
        const transferSdk = new Transfer({ region: region as Region });
        return from(transferSdk.getDropboxPreview({ dropboxId: action.dropboxId })).pipe(
          map(({ dropbox }) => {
            if (dropbox.status === 'Enabled') {
              return DropboxActions.GetDropboxPreviewSuccess({ dropbox });
            } else if (dropbox.status === 'Disabled') {
              this.router.navigateByUrl('/request-expired');
              return DropboxActions.GetDropboxPreviewSuccess({ dropbox });
            } else if (dropbox.status === 'Deleted') {
              this.router.navigateByUrl('/request-expired');
              return DropboxActions.GetDropboxPreviewSuccess({ dropbox });
            }
          }),
          catchError(error => {
            if (error instanceof GetDropboxPreviewError.NotFoundError) {
              this.router.navigateByUrl('/request-expired');
            } else if (error instanceof GetDropboxPreviewError.OwnerUsageExceededError) {
              this.router.navigateByUrl('/request-expired');
            } else if (error instanceof GetDropboxPreviewError.DropboxDeletedError) {
              this.router.navigateByUrl('/request-expired');
            } else if (error instanceof GetDropboxPreviewError.DropboxDisabledError) {
              this.router.navigateByUrl('/request-expired');
              } else if (error instanceof GetDropboxPreviewError.DropboxMaxTransferCountError) {
              this.router.navigateByUrl('/request-expired');
            } else if (error instanceof GetDropboxPreviewError.DropboxMaxBytesUploadedError) {
              this.router.navigateByUrl('/request-expired');
            } else {
              this.router.navigateByUrl('/request-expired');
            }
            return of(DropboxActions.GetDropboxPreviewFailure({ error: error.message }));
          })
        );
      })
    )
  );

}
